import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import ResponsivePagination from "react-responsive-pagination";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Footer from "../common/Footer";
import Sidebar from "../common/Sidebar";
import Oval from "../../loader/CircleLoade";
import * as Constants from "../Constants/index";

const reportColumns = [
  { label: "MemberName", index: 0 },
  { label: "MobileNumber", index: 1 },
  { label: "FirmName", index: 2 },
  { label: "RegistrationDate", index: 3, isDate: true },
  { label: "RegistrationType", index: 4 },
  { label: "status", index: 5 },
  { label: "ActiveStatus", index: 6 },
  { label: "totalPoints", index: 7, isPoints: true },
  { label: "Points", index: 8, isPoints: true },
  { label: "redeemblePoints", index: 9, isPoints: true },
  { label: "tdsDeducted", index: 10, isPoints: true },
  { label: "RegistrationId", index: 11 },
  { label: "Address1", index: 12 },
  { label: "Address2", index: 13 },
  { label: "State", index: 14 },
  { label: "District", index: 15 },
  { label: "City", index: 16 },
  { label: "PINCode", index: 17 },
  { label: "EmailId", index: 18 },
  { label: "AadhaarNumber", index: 19 },
  { label: "AadhaarFrontImage", index: 20, isLink: true },
  { label: "AadhaarBackImage", index: 21, isLink: true },
  { label: "PANNumber", index: 22 },
  { label: "PANImage", index: 23, isLink: true },
  { label: "UPIId", index: 24 },
  { label: "BankName", index: 25 },
  { label: "BankAccountNumber", index: 26 },
  { label: "AccountHolderName", index: 27 },
  { label: "AccountType", index: 28 },
  { label: "HeadQuarter", index: 29 },
  { label: "Designation", index: 30 },
  { label: "HeadQuarterDesignation", index: 31 },
  { label: "MemberTerritory", index: 32 },
  { label: "MemberCluster", index: 33 },
  { label: "MemberZone", index: 34 },
  { label: "MDOCode", index: 35 },
  { label: "MDOName", index: 36 },
  { label: "MDOMobile", index: 37 },
  { label: "TMCode", index: 38 },
  { label: "TMName", index: 39 },
  { label: "TMMobile", index: 40 },
  { label: "ClusterLeadCode", index: 41 },
  { label: "ClusterLeadName", index: 42 },
  { label: "ClusterLeadMobile", index: 43 },
];

const getCellValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (typeof value === "string" && value.trim().toLowerCase() === "null")
  ) {
    return "N/A";
  }

  return value;
};

const formatDateValue = (value) => {
  const displayValue = getCellValue(value);

  if (displayValue === "N/A") {
    return displayValue;
  }

  const parsedDate = moment(displayValue, [moment.ISO_8601, "YYYY-MM-DD"], true);
  return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : displayValue;
};

const formatPointsValue = (value) => {
  const displayValue = getCellValue(value);

  if (displayValue === "N/A") {
    return displayValue;
  }

  const numericValue = Number(displayValue);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : displayValue;
};

export default function NewEnrollmentReport() {
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterError, setFilterError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const limit = 10;
  const defaultFilters = {
    mobile: "",
    startDate: null,
    endDate: null,
  };

  useEffect(() => {
    fetchReport(currentPage);
  }, [currentPage]);

  const buildQueryString = (page, filters = {}) => {
    const activeFilters = {
      mobile,
      startDate,
      endDate,
      ...filters,
    };
    const params = new URLSearchParams();
    params.append("userType", "MEMBER");
    params.append("sortBy", "createDateTime");
    params.append("sortAsc", "true");
    params.append("limit", String(limit));
    params.append("page", String(page));

    if (activeFilters.mobile) {
      params.append("mobile", activeFilters.mobile);
    }

    if (activeFilters.startDate && activeFilters.endDate) {
      params.append(
        "start",
        moment(activeFilters.startDate).format("YYYY-MM-DD 00:00:00")
      );
      params.append(
        "end",
        moment(activeFilters.endDate).format("YYYY-MM-DD 23:59:59")
      );
    }

    return params.toString();
  };

  const fetchReport = async (page, filters = {}) => {
    setLoading(true);
    try {
      const url = `${Constants.baseUrl}reports/userreportnew/list?${buildQueryString(
        page,
        filters
      )}`;
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Accept-Language": localStorage.getItem("langauge"),
          appVersion: Constants.av,
          platform: Constants.pt,
          company: Constants.company,
          program: Constants.program,
        },
      });

      if (res.status === 200 || res.status === 201) {
        if (res.data.code === 10001 && res.data.output) {
          setResults(res.data.output.results || []);
          setTotalPages(res.data.output.totalPages || 0);
        } else if (res.data.code === 12002) {
          setResults([]);
          setTotalPages(0);
        } else {
          swal({
            text: res.data.msg,
            icon: "error",
            timer: 2000,
            buttons: false,
          });
        }
      }
    } catch (error) {
      console.log(error);
      swal({
        text: "Unable to load new enrollment report.",
        icon: "error",
        timer: 2000,
        buttons: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${Constants.baseUrl}reports/download/userReportNew?${buildQueryString(1)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Accept-Language": localStorage.getItem("langauge"),
            appVersion: Constants.av,
            platform: Constants.pt,
            company: Constants.company,
            program: Constants.program,
          },
          responseType: "blob",
        }
      );

      if (res.status === 200) {
        saveAs(new Blob([res.data]), "newEnrollmentReport.csv");
      }
    } catch (error) {
      console.log(error);
      swal({
        text: "Failed to download new enrollment report.",
        icon: "error",
        timer: 2000,
        buttons: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  const validateFilters = () => {
    if (startDate && !endDate) {
      setFilterError("Please select end date.");
      return false;
    }

    if (!startDate && endDate) {
      setFilterError("Please select start date.");
      return false;
    }

    if (startDate && endDate && moment(endDate).isBefore(moment(startDate), "day")) {
      setFilterError("End date should not be earlier than start date.");
      return false;
    }

    setFilterError("");
    return true;
  };

  const handleSearch = () => {
    if (!validateFilters()) {
      return;
    }

    const searchFilters = {
      mobile,
      startDate,
      endDate,
    };

    if (currentPage === 1) {
      fetchReport(1, searchFilters);
    } else {
      setCurrentPage(1);
    }
  };

  const handleReset = () => {
    setMobile(defaultFilters.mobile);
    setStartDate(defaultFilters.startDate);
    setEndDate(defaultFilters.endDate);
    setFilterError("");

    if (currentPage === 1) {
      fetchReport(1, defaultFilters);
    } else {
      setCurrentPage(1);
    }
  };

  return (
    <>
      {loading ? <Oval /> : null}

      <div
        style={{
          display: "flex",
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <Sidebar />
        </div>

        <div
          style={{
            flex: 1,
            padding: "20px",
            overflowX: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className="all-single-blog rpt"
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              flex: 1,
            }}
          >
            <div className="row">
              <div className="col-md-12 tab mb-20">
                <ul className="nav nav-tabs" role="tablist">
                  <li role="presentation" className="active">
                    <Link to="#Section1" role="tab" data-toggle="tab">
                      New Enrollment Report
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 col-sm-12 col-xs-12">
                <div className="form-group has-float-label style-two">
                  <label>Mobile No.</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Mobile No."
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                  />
                </div>
              </div>
              <div className="col-md-4 col-sm-12 col-xs-12">
                <div className="form-group has-float-label style-two">
                  <label>From Date</label>
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    maxDate={new Date()}
                    isClearable
                    placeholderText="Select start date"
                  />
                </div>
              </div>
              <div className="col-md-4 col-sm-12 col-xs-12">
                <div className="form-group has-float-label style-two">
                  <label>To Date</label>
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    maxDate={new Date()}
                    minDate={startDate || null}
                    isClearable
                    placeholderText="Select end date"
                  />
                </div>
              </div>
              <div className="col-md-12">
                {filterError ? (
                  <div
                    style={{
                      color: "#c0392b",
                      fontSize: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    {filterError}
                  </div>
                ) : null}
                <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="btn login_btn"
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      padding: "5px 18px",
                      fontSize: "12px",
                      borderRadius: "3px",
                      minWidth: "120px",
                    }}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="btn"
                    style={{
                      backgroundColor: "#7e7c7d",
                      color: "white",
                      padding: "5px 18px",
                      fontSize: "12px",
                      borderRadius: "3px",
                      minWidth: "120px",
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="carrd1" style={{ marginTop: "20px" }}>
                  <div
                    className="card-header card-header-warning"
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      padding: "10px 15px",
                      borderRadius: "4px 4px 0 0",
                    }}
                  >
                    <h4
                      className="card-title"
                      style={{
                        margin: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>New Enrollment Report</span>
                      {results.length > 0 && (
                        <Link
                          to="#"
                          className="pull-right btn btn-sm btn-light"
                          style={{ color: "#28a745", fontWeight: "bold" }}
                          onClick={downloadReport}
                        >
                          <i className="fa fa-file-excel-o"></i> Export
                        </Link>
                      )}
                    </h4>
                  </div>

                  <div
                    className="card-body table-responsive"
                    style={{ border: "1px solid #ddd", borderTop: "none" }}
                  >
                    <table className="table table-striped mb_01">
                      <thead>
                        <tr>
                          <th style={{ borderRight: "1px solid #dee2e6", whiteSpace: "nowrap" }}>S.No</th>
                          {reportColumns.map((column) => (
                            <th
                              key={`${column.index}-${column.label}`}
                              style={{
                                borderRight:
                                  column.index < reportColumns.length - 1
                                    ? "1px solid #dee2e6"
                                    : "none",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {results.length > 0 ? (
                          results.map((item, index) => {
                            const renderLink = (value) => {
                              const displayValue = getCellValue(value);

                              if (
                                typeof displayValue === "string" &&
                                displayValue.includes("http")
                              ) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => openImageModal(displayValue)}
                                    style={{
                                      color: "#0d6efd",
                                      textDecoration: "underline",
                                      background: "transparent",
                                      border: "none",
                                      padding: 0,
                                      cursor: "pointer",
                                      font: "inherit",
                                    }}
                                  >
                                    View
                                  </button>
                                );
                              }

                              return displayValue;
                            };

                            return (
                              <tr key={`${item[1] || "row"}-${index}`}>
                                <td style={{ borderRight: "1px solid #dee2e6" }}>
                                  {(currentPage - 1) * limit + index + 1}
                                </td>
                                {reportColumns.map((column) => (
                                  <td
                                    key={`${column.index}-${column.label}`}
                                    style={{
                                      borderRight:
                                        column.index < reportColumns.length - 1
                                          ? "1px solid #dee2e6"
                                          : "none",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {column.isLink
                                      ? renderLink(item[column.index])
                                      : column.isDate
                                        ? formatDateValue(item[column.index])
                                        : column.isPoints
                                          ? formatPointsValue(item[column.index])
                                        : getCellValue(item[column.index])}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={reportColumns.length + 1}
                              style={{
                                textAlign: "center",
                                fontSize: "16px",
                                padding: "20px",
                              }}
                            >
                              No record found!
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div
                      style={{
                        marginTop: "15px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {totalPages > 1 && (
                        <ResponsivePagination
                          current={currentPage}
                          total={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {isModalOpen && selectedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            padding: "16px",
          }}
          onClick={closeImageModal}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "1100px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeImageModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 10000,
                width: "42px",
                height: "42px",
                border: "none",
                borderRadius: "9999px",
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                color: "#fff",
                fontSize: "26px",
                lineHeight: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Close image preview"
            >
              X
            </button>
            <img
              src={selectedImage}
              alt="Document preview"
              style={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "10px",
                backgroundColor: "#fff",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
